import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Grid, TextField, Typography } from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import { AccountPicker } from '../LedgerPickers';
import DocResultDialog from './DocResultDialog';
import { InventoryItemPicker } from './InventoryShared';
import { fmtDate, getErrorMessage, itemLabelOf } from './InventoryShared';

const emptyForm = () => ({
  item_id: '',
  revaluation_date: new Date().toISOString().slice(0, 10),
  write_down_amount: '',
  loss_account_id: '',
  reason: '',
});

// Inventory revaluations are single-item WRITE-DOWNS (verified against
// CreateInventoryRevaluationDto): a positive amount capped at the stock
// value, posted DR loss account / CR inventory. Upward revaluations of
// physical assets live under Assets instead.
const RevaluationsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.INVENTORY, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  if (!can(RESOURCES.LEDGER.INVENTORY, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Revaluations.</Alert>;
  }

  const set = (key, value) => setValues((p) => ({ ...p, [key]: value }));
  const idOf = (d) => d?.revaluation_id ?? d?.id;
  const numberOf = (d) => d?.revaluation_number || (idOf(d) ? `REV-${idOf(d)}` : '');

  const submitCreate = async () => {
    if (!values.item_id) {
      showSnackbar('Item is required', 'error');
      return;
    }
    if (!(Number(values.write_down_amount) > 0)) {
      showSnackbar('Write-down amount must be greater than zero', 'error');
      return;
    }
    if (!values.loss_account_id) {
      showSnackbar('P&L loss account is required', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.createInvRevaluation({
        item_id: Number(values.item_id),
        revaluation_date: values.revaluation_date,
        write_down_amount: Number(values.write_down_amount),
        loss_account_id: Number(values.loss_account_id),
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
      });
      const doc = res?.data?.data ?? res?.data ?? {};
      setResult({ ...doc, id: idOf(doc) ?? doc.revaluation_id });
      setShowCreate(false);
      setValues(emptyForm());
      showSnackbar(`Revaluation ${numberOf(doc)} created as DRAFT`, 'success');
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <ModulePageHeader
        title="Revaluations"
        subtitle="Write stock down to recoverable value - DR loss account / CR inventory on posting."
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }}>
            New Revaluation
          </Button>,
        ] : null}
      />
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Revaluations are tracked by document number (returned on creation). The write-down is capped at the stock value.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Revaluation (Write-down)</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <InventoryItemPicker value={values.item_id} onChange={(id) => set('item_id', id)} required />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="date" label="Revaluation Date" value={values.revaluation_date}
                onChange={(e) => set('revaluation_date', e.target.value)} InputLabelProps={{ shrink: true }} required
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                fullWidth size="small" type="number" label="Write-down Amount" value={values.write_down_amount}
                onChange={(e) => set('write_down_amount', e.target.value)}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <AccountPicker label="P&L loss account" value={values.loss_account_id} onChange={(id) => set('loss_account_id', id)} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Reason" value={values.reason} onChange={(e) => set('reason', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <DocResultDialog
        open={!!result}
        onClose={() => setResult(null)}
        title="Revaluation"
        doc={result}
        setDoc={setResult}
        numberOf={numberOf}
        statusOf={(d) => d.status}
        api={{
          approve: (id) => ledgerAPI.approveInvRevaluation(id),
          post: (id) => ledgerAPI.postInvRevaluation(id),
          reverse: (id, d) => ledgerAPI.reverseInvRevaluation(id, d),
        }}
        canApprove={canApprove}
        renderSummary={(d) => (
          <>
            <Typography variant="body2" color="text.secondary">
              {d.item ? itemLabelOf(d.item) : `Item ${d.item_id ?? ''}`} - {fmtDate(d.revaluation_date)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Write-down: <strong>{formatAccountingAmount(d.write_down_amount)}</strong>
            </Typography>
            {d.reason && <Typography variant="body2" sx={{ mt: 1 }}>{d.reason}</Typography>}
          </>
        )}
      />

      {SnackbarComponent}
    </Box>
  );
};

export default RevaluationsTab;
